// All or portions of this file Copyright © 2019 Amazon Web Services, Inc. or its affiliates or licensors.
// All rights reserved. For full license terms and conditions, please see the LICENSE file at the root of
// this distribution (the "License") or go to https://content.sumerian.amazonaws.com/engine/latest/license.txt.
// All use of the software is governed by the License. All materials are made available on an "AS IS" BASIS, WITHOUT
// WARRANTIES OR CONDITIONS OF ANY KIND, express or implied. Do not remove or modify any license notices.
import TransformData from '../../animationpack/clip/TransformData';
import Vector3 from '../../math/Vector3';
import Quaternion from '../../math/Quaternion';
import Source from '../../animationpack/blendtree/Source';

/**
 * This tree source maintains its own source data, which can be modified directly using instance functions. This source is meant to be used for
 *        controlling a particular joint or set of joints programmatically.
 */
class ManagedTransformSource extends Source {
  /**
   * @param {string} [sourceName] Name of source we were initialized from, if given.
   */
  constructor(sourceName) {
    super();
    this._sourceName = sourceName || null;
    this._data = {};
  }

  /**
   * Sets a translation to the local transformdata for a given channelName. The channel has to be an instance of {@link TransformChannel}
   * @param {string} channelName
   * @param {Vector3} translation the translation to set
   */
  setTranslation(channelName, translation) {
    const channel = this._data[channelName];
    if (channel instanceof TransformData) {
      channel._translation.set(translation);
    }
  }

  /**
   * Gets the translation of the local transformdata for a given channelName. The channel has to be an instance of {@link TransformChannel}
   * @param {string} channelName
   * @param {Vector3} [store] to store the result in
   * @returns new Vector3 with result or store
   */
  getTranslation(channelName, store) {
    const channel = this._data[channelName];
    if (channel instanceof TransformData) {
      store = store || new Vector3();
      store.set(channel._translation);
    }
    return store;
  }

  /**
   * Sets a scale to the local transformdata for a given channelName. The channel has to be an instance of {@link TransformChannel}
   * @param {string} channelName
   * @param {Vector3} scale the scale to set
   */
  setScale(channelName, scale) {
    const channel = this._data[channelName];
    if (channel instanceof TransformData) {
      channel._scale.set(scale);
    }
  }

  /**
   * Gets the scale from the local transformdata for a given channelName. The channel has to be an instance of {@link TransformChannel}
   * @param {string} channelName
   * @param {Vector3} [store] to store the result in
   * @returns {Vector3} new vector with result or store
   */
  getScale(channelName, store) {
    const channel = this._data[channelName];
    if (channel instanceof TransformData) {
      store = store || new Vector3();
      store.set(channel._scale);
    }
    return store;
  }

  /**
   * Sets a rotation to the local transformdata for a given channelName. The channel has to be an instance of {@link TransformChannel}
   * @param {string} channelName
   * @param {Quaternion} rotation the rotation to set
   */
  setRotation(channelName, rotation) {
    const channel = this._data[channelName];
    if (channel instanceof TransformData) {
      channel._rotation.set(rotation);
    }
  }

  /**
   * Gets rotation from the local transformdata for a given channelName. The channel has to be an instance of {@link TransformChannel}
   * @param {string} channelName
   * @param {Quaternion} [store] to store the result in
   */
  getRotation(channelName, store) {
    const channel = this._data[channelName];
    if (channel instanceof TransformData) {
      store = store || new Quaternion();
      store.set(channel._rotation);
    }
    return store;
  }

  /**
   * Setup transform data for specific joints on this source, using the first frame from a given clip.
   * @param {AnimationClip} clip the animation clip to pull data from
   * @param {Array<string>} filter
   * @param {Array<String>} channelNames
   */
  initFromClip(clip, filter, channelNames) {
    if (filter === 'Include' && channelNames && channelNames.length) {
      for (var i = 0, max = channelNames.length; i < max; i++) {
        var channelName = channelNames[i];
        var channel = clip.findChannelByName(channelName);
        if (channel) {
          var data = channel.getData(0);
          this._data[channelName] = data;
        } else {
          console.error(`[Sumerian] Channel not in clip: ${channelName}`);
        }
      }
    } else {
      for (var i = 0, max = clip._channels.length; i < max; i++) {
        var channel = clip._channels[i];
        var channelName = channel._channelName;
        if (filter === 'Exclude'
          && channelNames
          && channelNames.length
          && channelNames.indexOf(channelName) > -1
        ) {
          var data = channel.getData(0);
          this._data[channelName] = data;
        }
      }
    }
  }

  getChannelData(channelName) {
    return this._data[channelName];
  }

  /*
   * @returns a source data mapping for the channels in this clip source
   */
  getSourceData() {
    return this._data;
  }

  /**
  * @returns {ManagedTransformSource}
  */
  clone() {
    const clonedData = {};
    for (const key in this._data) {
      clonedData[key] = this._data[key].clone();
    }

    return new ManagedTransformSource(this._sourceName, clonedData);
  }
}

export default ManagedTransformSource;
